// @ts-nocheck
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { ReactRenderer } from '@tiptap/react';
import tippy from 'tippy.js';
import { MenuItem, MenuList, Paper, Typography } from '@mui/material';
import PropTypes from 'prop-types';

const LOADING_ITEM = {
  id: '__loading__',
  label: 'Loading...',
  isHint: true,
  isLoading: true,
};

const shouldShowLoading = (props) =>
  Boolean(props.query?.trim()) &&
  props.text?.includes(':') &&
  (props.items || []).length === 0;

/**
 * Suggestion dropdown rendered by Tiptap for chat/editor mentions.
 *
 * @param {object} props - Props passed by Tiptap's suggestion renderer.
 * @param {Array<object|string>} props.items - Suggestion rows. Hint rows use `isHint`
 *   and are displayed disabled; an empty list displays "No result".
 * @param {Function} props.command - Tiptap callback used to insert the selected mention.
 * @param {React.Ref<object>} ref - Imperative keyboard API consumed by Tiptap.
 * @returns {JSX.Element} Popup menu with keyboard-highlighted mention suggestions.
 */
const MentionListForChat = forwardRef(function MentionListForChat(props, ref) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectableItems = (props.items || []).filter((item) => !item.isHint);

  /**
   * Inserts a selectable suggestion by index. Hint items are filtered out so
   * keyboard navigation cannot insert instructional rows.
   *
   * @param {number} index - Index in the selectable suggestion list.
   * @returns {void}
   */
  const selectItem = (index) => {
    const item = selectableItems[index];
    if (item) {
      props.command({ id: item.name, ...item });
    }
  };

  /**
   * Moves the active keyboard selection one row up, wrapping at the top.
   *
   * @returns {void}
   */
  const upHandler = () => {
    setSelectedIndex(
      (selectedIndex + selectableItems.length - 1) % selectableItems.length
    );
  };

  /**
   * Moves the active keyboard selection one row down, wrapping at the bottom.
   *
   * @returns {void}
   */
  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % selectableItems.length);
  };

  /**
   * Inserts the currently highlighted suggestion when Enter is pressed.
   *
   * @returns {void}
   */
  const enterHandler = () => {
    selectItem(selectedIndex);
  };

  useEffect(() => setSelectedIndex(0), [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        upHandler();
        return true;
      }
      if (event.key === 'ArrowDown') {
        downHandler();
        return true;
      }
      if (event.key === 'Enter') {
        enterHandler();
        return true;
      }
      return false;
    },
  }));

  const getOnClickHandler = useCallback(
    (index) => () => selectItem(index),
    [selectableItems]
  );

  const items = props.items || [];

  return (
    <Paper
      sx={{
        width: 350,
        maxWidth: '100%',
        maxHeight: 300,
        overflow: 'auto',
        border: '1px solid rgba(0,0,0, 0.1)',
      }}
      elevation={0}
    >
      <MenuList>
        {items.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No result
            </Typography>
          </MenuItem>
        ) : (
          items.map((item, index) => {
            if (item.isHint) {
              return (
                <MenuItem key={item.id} disabled>
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                </MenuItem>
              );
            }
            const itemLabel =
              typeof item === 'object'
                ? item.label || item.name || item.Name
                : item;
            const itemKey = typeof item === 'object' ? item.id : item;
            return (
              <MenuItem
                className={index === selectedIndex ? 'is-selected' : ''}
                key={itemKey}
                onClick={getOnClickHandler(index)}
                sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
              >
                {itemLabel}
              </MenuItem>
            );
          })
        )}
      </MenuList>
    </Paper>
  );
});

MentionListForChat.displayName = 'MentionListForChat';

MentionListForChat.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isHint: PropTypes.bool,
        isLoading: PropTypes.bool,
      }),
    ])
  ),
  command: PropTypes.func.isRequired,
};

/**
 * Creates the Tiptap suggestion renderer lifecycle for mention popups.
 *
 * The returned object is consumed by `@tiptap/extension-mention` and owns the
 * ReactRenderer instance plus the tippy popup positioning.
 *
 * @returns {object} Tiptap suggestion renderer hooks: onStart, onUpdate,
 * onKeyDown, and onExit.
 */
const renderMentionListForChat = () => {
  let component;
  let popup;

  const getPropsWithLoadingState = (props) => ({
    ...props,
    items: shouldShowLoading(props) ? [LOADING_ITEM] : props.items,
  });

  const mountPopup = (props) => {
    component = new ReactRenderer(MentionListForChat, {
      props,
      editor: props.editor,
    });

    if (!props.clientRect) return;

    popup = tippy('body', {
      getReferenceClientRect: props.clientRect,
      appendTo: () => document.body,
      content: component.element,
      showOnCreate: true,
      interactive: true,
      trigger: 'manual',
      placement: 'bottom-start',
    });
  };

  return {
    onBeforeStart: (props) => {
      if (shouldShowLoading(props)) {
        mountPopup(getPropsWithLoadingState(props));
      }
    },

    onStart: (props) => {
      if (component) {
        component.updateProps(props);
        return;
      }

      mountPopup(props);
    },

    onBeforeUpdate: (props) => {
      if (component && shouldShowLoading(props)) {
        component.updateProps(getPropsWithLoadingState(props));
      }
    },

    onUpdate: (props) => {
      component.updateProps(props);
      if (!props.clientRect) return;
      popup[0].setProps({ getReferenceClientRect: props.clientRect });
    },

    onKeyDown(props) {
      if (props.event.key === 'Escape') {
        popup[0].hide();
        return true;
      }
      return component.ref?.onKeyDown(props);
    },

    onExit() {
      popup[0].destroy();
      component.destroy();
      // Clear refs so the next `@` trigger mounts a fresh popup instead of reusing a destroyed one.
      popup = undefined;
      component = undefined;
    },
  };
};

export default renderMentionListForChat;
